import { useCauserie } from '@contexts/CauserieContext/CauserieContext';
import { showToast } from '@design-system';
import { useGetTalksMutation, useGetUsersMutation, useUploadFileMutation, usePostResultsMutation } from '@services';
import { transformSignatureToFile } from '@utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

export const useCauseriesTalkEmargementPageVM = () => {
    const { thematicId, talkId } = useParams();
    const navigate = useNavigate();
    const { setSignature, setRequestUser, requestUser, signature, questionsAnswers } = useCauserie();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');
    const [isEmpty, setIsEmpty] = useState<boolean>(true);
    const [isSomeoneElse, setIsSomeoneElse] = useState<boolean>(false);
    const [instructorName, setInstructorName] = useState<string>('');
    const signatureCanvasRef = useRef<SignatureCanvas>(null);
    const [usersOptions, setUsersOptions] = useState<Array<{ id: string; name: string }>>([]);

    const [getTalks, { isLoading: isLoadingTalks, data: talks }] = useGetTalksMutation();
    const [uploadFile, { isError: isFileUploadError, error: fileUploadError, data: fileUploadData }] = useUploadFileMutation();
    const [getUsers, { data: users }] = useGetUsersMutation();
    const [postResults, { isLoading: isLoadingPostResults, data: postResultsData, error: postResultsError }] = usePostResultsMutation();

    const handleIsSomeoneElseChange = (checked: boolean) => {
        setIsSomeoneElse(checked);
    };

    const handleRequestUserChange = (value: string) => {
        setRequestUser(value);
    };

    const handleInstructorNameChange = (value: string) => {
        setInstructorName(value);
    };

    const handleClearSignature = () => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
            setIsEmpty(true);
            setSignature('');
        }
    };

    const handleSaveSignature = () => {
        if (signatureCanvasRef.current) {
            const isEmpty = signatureCanvasRef.current.isEmpty();
            if (isEmpty) {
                showToast({
                    message: 'Veuillez dessiner votre signature avant de continuer',
                    type: 'error'
                });
                return;
            }

            const dataUrl = signatureCanvasRef.current.toDataURL('image/png');
            const file = transformSignatureToFile(dataUrl);

            const formData = new FormData();
            formData.append('file', file);

            uploadFile(formData);
        }
    };

    const handleClickSave = () => {
        if (!instructorName) {
            showToast({
                message: 'Veuillez remplir tous les champs obligatoires',
                type: 'error'
            });
            return;
        }

        if (isSomeoneElse && !requestUser) {
            showToast({
                message: 'Veuillez sélectionner ou saisir le nom de la personne',
                type: 'error'
            });
            return;
        }

        if (isSomeoneElse && !isEmpty) {
            handleSaveSignature();
        } else if (isSomeoneElse && isEmpty) {
            showToast({
                message: 'Veuillez signer avant de continuer',
                type: 'error'
            });
        } else {
            if (!talkId) return;

            const payload = {
                talkId: parseInt(talkId),
                answers: questionsAnswers,
                ...(signature && { signature }),
                ...(instructorName && { instructor_name: instructorName })
            };

            postResults(payload);
        }
    };

    const isDisabled = useMemo(
        () => (isSomeoneElse && (isEmpty || !requestUser)) || !instructorName,
        [isSomeoneElse, isEmpty, requestUser, instructorName]
    );

    useEffect(() => {
        if (!thematicId) return;
        getTalks({ thematicId: parseInt(thematicId) });
        getUsers(undefined);
    }, [thematicId, getTalks, getUsers]);

    useEffect(() => {
        if (!talks || !talkId) return;

        const currentTalk = talks.find((talk) => talk.id === parseInt(talkId));

        if (!currentTalk) {
            setErrorMessage('Causerie non trouvée');
            return;
        }

        setPageTitle(currentTalk.title);
    }, [talks, talkId]);

    useEffect(() => {
        if (users) {
            const allUsers = users.operators.flatMap((operator) => operator.worksites.flatMap(() => []));
            setUsersOptions(allUsers);
        }
    }, [users]);

    useEffect(() => {
        if (isFileUploadError && fileUploadError) {
            console.error('fileUploadError:', fileUploadError);
        }
        if (fileUploadData) {
            setSignature(fileUploadData.original);
            if (!talkId) return;

            // Vérifier si requestUser est un ID (number) ou un nom (string créé)
            const requestUserId = requestUser && !isNaN(Number(requestUser)) ? parseInt(requestUser) : undefined;
            const requestUserName = requestUser && isNaN(Number(requestUser)) ? requestUser : undefined;

            const payload = {
                talkId: parseInt(talkId),
                answers: questionsAnswers,
                signature: fileUploadData.original,
                ...(instructorName && { instructor_name: instructorName }),
                ...(requestUserId && { request_user_id: requestUserId }),
                ...(requestUserName && { request_user: requestUserName })
            };

            postResults(payload);
        }
    }, [
        fileUploadData,
        isFileUploadError,
        fileUploadError,
        setSignature,
        talkId,
        postResults,
        instructorName,
        questionsAnswers,
        requestUser
    ]);

    useEffect(() => {
        if (isLoadingPostResults) {
            return;
        }

        if (postResultsError) {
            setErrorMessage('Une erreur est survenue lors de la soumission des réponses');
            return;
        }

        if (postResultsData) {
            showToast({ type: 'success', message: 'Causerie validée avec succès' });
            navigate('/sante-securite/causeries/list');
        }
    }, [isLoadingPostResults, postResultsError, postResultsData, navigate]);

    useEffect(() => {
        if (!isSomeoneElse) {
            setIsEmpty(true);
        }
    }, [isSomeoneElse]);

    return {
        errorMessage,
        pageTitle,
        isLoadingTalks,
        talks,
        isSomeoneElse,
        handleIsSomeoneElseChange,
        handleClearSignature,
        handleSaveSignature,
        isEmpty,
        signatureCanvasRef,
        setIsEmpty,
        handleClickSave,
        isDisabled,
        usersOptions,
        requestUser,
        handleRequestUserChange,
        instructorName,
        handleInstructorNameChange
    };
};
